import { useState } from "react";
import { cn } from "@/lib/cn";

/**
 * Glass Lab — iterative liquid glass preview across backgrounds and themes.
 * Tune parameters until the right look is found, then apply to index.css.
 */

/* ─── SVG filters ─────────────────────────────────────────────────────────── */
function GlassFilters() {
  return (
    <svg style={{ display: "none" }} aria-hidden="true">
      <defs>
        <filter id="lg-subtle" x="-8%" y="-8%" width="116%" height="116%" colorInterpolationFilters="sRGB">
          <feTurbulence type="fractalNoise" baseFrequency="0.014 0.010" numOctaves="3" seed="5" result="noise" />
          <feGaussianBlur in="noise" stdDeviation="3" result="softNoise" />
          <feDisplacementMap in="SourceGraphic" in2="softNoise" scale="10" xChannelSelector="R" yChannelSelector="G" result="displaced" />
          <feSpecularLighting in="softNoise" surfaceScale="2" specularConstant="0.4" specularExponent="25" lightingColor="rgba(255,255,255,0.8)" result="specular">
            <fePointLight x="-400" y="-900" z="2000" />
          </feSpecularLighting>
          <feBlend in="displaced" in2="specular" mode="screen" result="blended" />
          <feComposite in="blended" in2="SourceGraphic" operator="in" />
        </filter>
        <filter id="lg-medium" x="-10%" y="-10%" width="120%" height="120%" colorInterpolationFilters="sRGB">
          <feTurbulence type="fractalNoise" baseFrequency="0.012 0.009" numOctaves="3" seed="5" result="noise" />
          <feGaussianBlur in="noise" stdDeviation="2.5" result="softNoise" />
          <feDisplacementMap in="SourceGraphic" in2="softNoise" scale="22" xChannelSelector="R" yChannelSelector="G" result="displaced" />
          <feSpecularLighting in="softNoise" surfaceScale="3" specularConstant="0.55" specularExponent="28" lightingColor="rgba(255,255,255,0.85)" result="specular">
            <fePointLight x="-400" y="-800" z="1800" />
          </feSpecularLighting>
          <feBlend in="displaced" in2="specular" mode="screen" result="blended" />
          <feComposite in="blended" in2="SourceGraphic" operator="in" />
        </filter>
      </defs>
    </svg>
  );
}

/* ─── Backgrounds ─────────────────────────────────────────────────────────── */
const BACKGROUNDS = {
  "dark-canvas": {
    label: "Dark Canvas",
    isDark: true,
    css: `
      radial-gradient(ellipse 55% 45% at 15% 25%, rgba(48,209,88,0.14) 0%, transparent 70%),
      radial-gradient(ellipse 50% 40% at 85% 75%, rgba(10,132,255,0.12) 0%, transparent 70%),
      radial-gradient(ellipse 40% 35% at 75% 8%, rgba(167,139,250,0.08) 0%, transparent 65%),
      #0A0A0F
    `,
  },
  "light-canvas": {
    label: "Light Canvas",
    isDark: false,
    css: `
      radial-gradient(ellipse 55% 45% at 15% 25%, rgba(48,209,88,0.20) 0%, transparent 70%),
      radial-gradient(ellipse 50% 40% at 85% 75%, rgba(10,132,255,0.16) 0%, transparent 70%),
      radial-gradient(ellipse 40% 35% at 75% 8%, rgba(167,139,250,0.12) 0%, transparent 65%),
      #F2F2F7
    `,
  },
  "colorful": {
    label: "Colorful",
    isDark: true,
    css: `
      radial-gradient(ellipse 55% 50% at 20% 25%, rgba(48,209,88,0.65) 0%, transparent 60%),
      radial-gradient(ellipse 50% 45% at 80% 70%, rgba(10,132,255,0.60) 0%, transparent 60%),
      radial-gradient(ellipse 45% 55% at 50% 5%,  rgba(167,139,250,0.55) 0%, transparent 60%),
      radial-gradient(ellipse 45% 40% at 5% 85%,  rgba(255,149,0,0.45) 0%, transparent 60%),
      #080808
    `,
  },
  "ios-blue": {
    label: "iOS Blue",
    isDark: true,
    css: `
      radial-gradient(ellipse 80% 60% at 30% 20%, rgba(30,100,220,0.85) 0%, transparent 70%),
      radial-gradient(ellipse 70% 80% at 70% 70%, rgba(15,70,180,0.90) 0%, transparent 65%),
      radial-gradient(ellipse 50% 50% at 80% 10%, rgba(100,180,255,0.55) 0%, transparent 60%),
      #0d3fa6
    `,
  },
  "sunset": {
    label: "Sunset",
    isDark: true,
    css: `
      radial-gradient(ellipse 70% 55% at 20% 30%, rgba(255,100,50,0.70) 0%, transparent 65%),
      radial-gradient(ellipse 60% 70% at 80% 60%, rgba(180,50,120,0.60) 0%, transparent 65%),
      radial-gradient(ellipse 50% 40% at 50% 90%, rgba(255,200,50,0.40) 0%, transparent 60%),
      #160818
    `,
  },
} as const;

type BgKey = keyof typeof BACKGROUNDS;

/* ─── Glass config ────────────────────────────────────────────────────────── */
interface GlassConfig {
  label: string;
  blur: number;
  saturate: number;
  brightness: number;
  fillOpacity: number;
  filterId: string | null;
  rimStrength: number;
  innerGlow: boolean;
  /** "none" = no border, "linear" = diagonal gradient, "conic" = physically correct light wrap */
  rimBorder: "none" | "linear" | "conic";
}

const CONFIGS: GlassConfig[] = [
  {
    label: "Current",
    blur: 20, saturate: 150, brightness: 1.00,
    fillOpacity: 0.22, filterId: "lg-medium",
    rimStrength: 0.14, innerGlow: false, rimBorder: "none",
  },
  {
    label: "iOS + linear rim",
    blur: 10, saturate: 220, brightness: 1.12,
    fillOpacity: 0.06, filterId: "lg-subtle",
    rimStrength: 0.45, innerGlow: true, rimBorder: "linear",
  },
  {
    label: "iOS + conic rim",
    blur: 10, saturate: 220, brightness: 1.12,
    fillOpacity: 0.06, filterId: "lg-subtle",
    rimStrength: 0.45, innerGlow: true, rimBorder: "conic",
  },
];

/* ─── Helpers — adapt glass to dark/light context ─────────────────────────── */
function glassFill(cfg: GlassConfig, isDark: boolean) {
  return isDark
    ? `rgba(255,255,255,${cfg.fillOpacity})`
    : `rgba(255,255,255,${Math.max(0.60, cfg.fillOpacity + 0.55)})`;
}

function glassBorder(cfg: GlassConfig, isDark: boolean) {
  return isDark
    ? `1px solid rgba(255,255,255,${cfg.rimStrength * 0.5})`
    : `1px solid rgba(0,0,0,${cfg.rimStrength * 0.10})`;
}

function glassShadow(cfg: GlassConfig, isDark: boolean) {
  const parts = isDark
    ? [
        "0 8px 32px rgba(0,0,0,0.35)",
        `inset 0 1px 0 rgba(255,255,255,${cfg.rimStrength})`,
        cfg.innerGlow ? "inset 0 0 20px rgba(255,255,255,0.04)" : null,
      ]
    : [
        "0 4px 24px rgba(0,0,0,0.10)",
        "0 1px 4px rgba(0,0,0,0.06)",
        "inset 0 1px 0 rgba(255,255,255,0.85)",
        cfg.innerGlow ? "inset 0 0 20px rgba(255,255,255,0.20)" : null,
      ];
  return parts.filter(Boolean).join(", ");
}

function glassFaceGradient(cfg: GlassConfig, isDark: boolean) {
  const top = isDark ? cfg.rimStrength * 0.55 : 0.30;
  return `linear-gradient(145deg, rgba(255,255,255,${top}) 0%, rgba(255,255,255,0.02) 40%, rgba(255,255,255,0.00) 100%)`;
}

function textColor(isDark: boolean) {
  return isDark ? "rgba(255,255,255,0.90)" : "rgba(0,0,0,0.85)";
}
function subTextColor(isDark: boolean) {
  return isDark ? "rgba(255,255,255,0.50)" : "rgba(0,0,0,0.40)";
}

/* ─── Glass primitives ────────────────────────────────────────────────────── */
function GlassBox({ cfg, isDark, children, className, style }: {
  cfg: GlassConfig; isDark: boolean; children: React.ReactNode;
  className?: string; style?: React.CSSProperties;
}) {
  const bdf = cfg.filterId
    ? `url(#${cfg.filterId}) saturate(${cfg.saturate}%) brightness(${cfg.brightness}) blur(${cfg.blur}px)`
    : `saturate(${cfg.saturate}%) brightness(${cfg.brightness}) blur(${cfg.blur}px)`;

  return (
    <div
      className={cn("relative overflow-hidden rounded-[22px]", className)}
      style={{
        backdropFilter: bdf,
        WebkitBackdropFilter: `saturate(${cfg.saturate}%) brightness(${cfg.brightness}) blur(${cfg.blur}px)`,
        background: glassFill(cfg, isDark),
        border: glassBorder(cfg, isDark),
        boxShadow: glassShadow(cfg, isDark),
        ...style,
      }}
    >
      {/* Face gradient — curved surface highlight */}
      <div className="pointer-events-none absolute inset-0 z-10 rounded-[inherit]"
        style={{ background: glassFaceGradient(cfg, isDark) }} />

      {/* Rim border — linear diagonal (old approach) */}
      {cfg.rimBorder === "linear" && (
        <div className="pointer-events-none absolute inset-0 z-20 rounded-[inherit]"
          style={{
            padding: "1px",
            background: `linear-gradient(145deg,
              rgba(255,255,255,${cfg.rimStrength}) 0%,
              rgba(255,255,255,${cfg.rimStrength * 0.3}) 30%,
              rgba(255,255,255,0.02) 65%,
              rgba(255,255,255,0.06) 100%)`,
            WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            WebkitMaskComposite: "xor",
            maskComposite: "exclude",
          }} />
      )}

      {/* Rim border — conic (physically correct: light wraps the perimeter)
          Light source: top-left (315deg). Rim is bright where the edge faces
          the light, fades as the surface turns away — like real glass edges. */}
      {cfg.rimBorder === "conic" && (
        <div className="pointer-events-none absolute inset-0 z-20 rounded-[inherit]"
          style={{
            padding: "1.5px",
            background: `conic-gradient(
              from 315deg,
              rgba(255,255,255,${cfg.rimStrength})      0deg,
              rgba(255,255,255,${cfg.rimStrength * 0.9}) 45deg,
              rgba(255,255,255,${cfg.rimStrength * 0.22}) 90deg,
              rgba(255,255,255,0.03)                     135deg,
              rgba(255,255,255,0.00)                     180deg,
              rgba(255,255,255,0.00)                     225deg,
              rgba(255,255,255,0.06)                     270deg,
              rgba(255,255,255,${cfg.rimStrength * 0.45}) 315deg,
              rgba(255,255,255,${cfg.rimStrength})       360deg
            )`,
            WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            WebkitMaskComposite: "xor",
            maskComposite: "exclude",
          }} />
      )}

      <div className="relative z-30">{children}</div>
    </div>
  );
}

function GlassCircle({ cfg, isDark, active, children }: {
  cfg: GlassConfig; isDark: boolean; active?: boolean; children: React.ReactNode;
}) {
  if (active) {
    return (
      <div className="relative flex h-16 w-16 items-center justify-center overflow-hidden rounded-full"
        style={{ background: "rgba(255,255,255,0.92)", boxShadow: "0 4px 16px rgba(0,0,0,0.20), inset 0 1px 0 rgba(255,255,255,0.90)" }}>
        <div style={{ color: isDark ? "#1d6fe8" : "#2563eb" }}>{children}</div>
      </div>
    );
  }
  const bdf = cfg.filterId
    ? `url(#${cfg.filterId}) saturate(${cfg.saturate}%) brightness(${cfg.brightness}) blur(${cfg.blur}px)`
    : `saturate(${cfg.saturate}%) brightness(${cfg.brightness}) blur(${cfg.blur}px)`;
  return (
    <div className="relative flex h-16 w-16 items-center justify-center overflow-hidden rounded-full"
      style={{
        backdropFilter: bdf,
        WebkitBackdropFilter: `saturate(${cfg.saturate}%) brightness(${cfg.brightness}) blur(${cfg.blur}px)`,
        background: glassFill(cfg, isDark),
        border: glassBorder(cfg, isDark),
        boxShadow: glassShadow(cfg, isDark),
      }}>
      <div className="pointer-events-none absolute inset-0"
        style={{ background: glassFaceGradient(cfg, isDark) }} />
      <div className="relative z-10" style={{ color: isDark ? "rgba(255,255,255,0.80)" : "rgba(0,0,0,0.65)" }}>
        {children}
      </div>
    </div>
  );
}

/* ─── Control Center panel ────────────────────────────────────────────────── */
function ControlPanel({ cfg, isDark }: { cfg: GlassConfig; isDark: boolean }) {
  const tx = { color: textColor(isDark) };
  const stx = { color: subTextColor(isDark) };

  return (
    <div className="flex w-[230px] flex-col gap-3">
      {/* Row 1: WiFi + Now Playing */}
      <div className="flex gap-3">
        <GlassBox cfg={cfg} isDark={isDark} className="flex flex-1 items-center gap-2 px-3 py-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/90">
            <svg className="h-4 w-4 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
              <path d="M1.41 7.37C5.22 3.57 10.39 1.5 12 1.5c1.61 0 6.78 2.07 10.59 5.87l-2.12 2.12C17.44 6.46 14.1 4.5 12 4.5c-2.1 0-5.44 1.96-8.47 4.99L1.41 7.37zm4.24 4.24C7.44 9.82 9.67 9 12 9c2.33 0 4.56.82 6.35 2.61l2.12-2.12C18.33 7.35 15.34 6 12 6S5.67 7.35 3.53 9.49l2.12 2.12zm4.24 4.24C10.52 14.85 11.23 14.5 12 14.5c.77 0 1.48.35 2.11.85l2.12-2.12C15.05 12.09 13.59 11.5 12 11.5s-3.05.59-4.23 1.73l2.12 2.12zM12 17.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5z" />
            </svg>
          </div>
          <div>
            <div className="text-xs font-bold" style={tx}>Wi-Fi</div>
            <div className="text-[10px]" style={stx}>Home</div>
          </div>
        </GlassBox>
        <GlassBox cfg={cfg} isDark={isDark} className="flex-1 p-3">
          <div className="mb-2 flex items-start gap-2">
            <div className="h-7 w-7 shrink-0 rounded-lg bg-gradient-to-br from-red-500 to-orange-400" />
            <div className="min-w-0">
              <div className="truncate text-xs font-bold" style={tx}>Besties</div>
              <div className="truncate text-[10px]" style={stx}>Black Country…</div>
            </div>
          </div>
          <div className="flex items-center justify-between">
            {[
              "M6 6h2v12H6zm3.5 6 8.5 6V6z",
              "M8 5v14l11-7z",
              "M18 6h-2v12h2zm-3.5 6L6 6v12z",
            ].map((d, i) => (
              <svg key={i} className={cn("w-4 h-4", i === 1 ? "w-5 h-5"  : "")}
                viewBox="0 0 24 24" fill="currentColor"
                style={{ color: i === 1 ? textColor(isDark) : subTextColor(isDark) }}>
                <path d={d} />
              </svg>
            ))}
          </div>
        </GlassBox>
      </div>

      {/* Row 2: circles + Focus */}
      <div className="flex gap-3">
        <GlassCircle cfg={cfg} isDark={isDark} active>
          <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.71 7.71 12 2h-1v7.59L6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 11 14.41V22h1l5.71-5.71-4.3-4.29 4.3-4.29zM13 5.83l1.88 1.88L13 9.59V5.83zm1.88 10.46L13 18.17v-3.76l1.88 1.88z" />
          </svg>
        </GlassCircle>
        <GlassCircle cfg={cfg} isDark={isDark} active>
          <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z" />
          </svg>
        </GlassCircle>
        <GlassBox cfg={cfg} isDark={isDark} className="flex flex-1 items-center gap-2 px-3">
          <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="currentColor" style={stx}>
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm3-8a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" />
          </svg>
          <span className="text-xs font-semibold" style={stx}>Focus</span>
        </GlassBox>
      </div>

      {/* Sliders */}
      {["Display", "Sound"].map((label, i) => (
        <GlassBox key={label} cfg={cfg} isDark={isDark} className="p-3">
          <div className="mb-2 text-xs font-semibold" style={tx}>{label}</div>
          <div className="flex items-center gap-2">
            <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="currentColor" style={stx}>
              <circle cx="12" cy="12" r="5" />
              <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
            </svg>
            <div className="relative h-1.5 flex-1 rounded-full" style={{ background: isDark ? "rgba(255,255,255,0.22)" : "rgba(0,0,0,0.12)" }}>
              <div className="absolute left-0 top-0 h-full rounded-full" style={{ width: i === 0 ? "68%" : "75%", background: isDark ? "rgba(255,255,255,0.75)" : "rgba(0,0,0,0.50)" }} />
              <div className="absolute top-1/2 h-4 w-4 -translate-y-1/2 rounded-full bg-white shadow"
                style={{ left: `calc(${i === 0 ? 68 : 75}% - 8px)` }} />
            </div>
            <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="currentColor" style={{ color: isDark ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.45)" }}>
              <circle cx="12" cy="12" r="5" />
              <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
            </svg>
          </div>
        </GlassBox>
      ))}

      <div className="text-center font-mono text-[9px]" style={stx}>
        blur={cfg.blur} · sat={cfg.saturate}% · fill={Math.round(cfg.fillOpacity * 100)}%
      </div>
    </div>
  );
}

/* ─── Page ─────────────────────────────────────────────────────────────────── */
type Tab = "compare" | "tunables" | "bg-tune";

export function GlassLabPage() {
  const [bg, setBg] = useState<BgKey>("dark-canvas");
  const [tab, setTab] = useState<Tab>("compare");

  // Glass tunables
  const [blur, setBlur] = useState(10);
  const [saturate, setSaturate] = useState(220);
  const [brightness, setBrightness] = useState(112);
  const [fill, setFill] = useState(6);
  const [rim, setRim] = useState(45);

  // Background tunables
  const [bgLightness, setBgLightness] = useState(4);    // base darkness 2–20
  const [bgHue, setBgHue] = useState(240);               // base hue 0–360
  const [bgGreen, setBgGreen] = useState(14);            // blob opacities 0–80
  const [bgBlue, setBgBlue] = useState(12);
  const [bgPurple, setBgPurple] = useState(8);
  const [bgOrange, setBgOrange] = useState(0);

  const tunableBgCss = `
    radial-gradient(ellipse 55% 45% at 15% 25%, rgba(48,209,88,${bgGreen / 100}) 0%, transparent 70%),
    radial-gradient(ellipse 50% 40% at 85% 75%, rgba(10,132,255,${bgBlue / 100}) 0%, transparent 70%),
    radial-gradient(ellipse 40% 35% at 75% 8%, rgba(167,139,250,${bgPurple / 100}) 0%, transparent 65%),
    radial-gradient(ellipse 45% 40% at 5% 85%, rgba(255,149,0,${bgOrange / 100}) 0%, transparent 60%),
    hsl(${bgHue},10%,${bgLightness}%)
  `.trim();

  const activeBg = tab === "bg-tune" ? { isDark: true, css: tunableBgCss } : BACKGROUNDS[bg];
  const { isDark, css } = activeBg;

  const tunableCfg: GlassConfig = {
    label: "Custom",
    blur, saturate, brightness: brightness / 100,
    fillOpacity: fill / 100,
    filterId: "lg-subtle",
    rimStrength: rim / 100,
    innerGlow: true, rimBorder: "conic",
  };

  const labelColor = isDark ? "text-white/60" : "text-black/50";
  const headColor  = isDark ? "text-white"    : "text-black/80";
  const chipActive = isDark ? "bg-white/20 text-white" : "bg-black/10 text-black/80";
  const chipIdle   = isDark ? "bg-white/5 text-white/35 hover:bg-white/10 hover:text-white/60"
                            : "bg-black/5 text-black/30 hover:bg-black/8 hover:text-black/55";

  return (
    <div className="relative min-h-screen overflow-hidden">
      <GlassFilters />
      <div className="absolute inset-0 transition-all duration-500" style={{ background: css }} />

      <div className="relative z-10 mx-auto max-w-5xl px-8 py-8">
        {/* Header */}
        <div className="mb-5">
          <h1 className={cn("mb-0.5 text-xl font-bold", headColor)}>Liquid Glass Lab</h1>
          <p className={cn("text-xs", labelColor)}>Compare glass styles and backgrounds. Dial in exact values with Live Tune.</p>
        </div>

        {/* Controls row */}
        <div className="mb-8 flex flex-wrap items-center gap-3">
          {/* Background switcher — hidden when tuning bg */}
          {tab !== "bg-tune" && (
            <div className="flex gap-1.5">
              {(Object.entries(BACKGROUNDS) as [BgKey, typeof BACKGROUNDS[BgKey]][]).map(([key, val]) => (
                <button key={key} onClick={() => setBg(key)}
                  className={cn("rounded-full px-3 py-1 text-[11px] font-semibold transition-all", bg === key ? chipActive : chipIdle)}
                  data-testid={`bg-${key}`}>
                  {val.label}
                </button>
              ))}
              <div className={cn("mx-2 h-4 w-px self-center", "bg-white/15")} />
            </div>
          )}

          {/* Tab switcher */}
          <div className="flex gap-1.5">
            {([["compare", "Compare"], ["tunables", "Tune Glass"], ["bg-tune", "Tune BG"]] as [Tab, string][]).map(([t, l]) => (
              <button key={t} onClick={() => setTab(t)}
                className={cn("rounded-full px-3 py-1 text-[11px] font-semibold transition-all", tab === t ? chipActive : chipIdle)}
                data-testid={`tab-${t}`}>
                {l}
              </button>
            ))}
          </div>
        </div>

        {/* ── COMPARE ── */}
        {tab === "compare" && (
          <div className="flex flex-wrap gap-8 items-start">
            {CONFIGS.map((cfg, i) => (
              <div key={i}>
                <div className={cn("mb-2 text-xs font-semibold", labelColor)}>{cfg.label}</div>
                <ControlPanel cfg={cfg} isDark={isDark} />
              </div>
            ))}
          </div>
        )}

        {/* ── TUNE GLASS ── */}
        {tab === "tunables" && (
          <div className="flex gap-10 items-start">
            <div className="w-60 space-y-4">
              <div className={cn("mb-1 text-xs font-semibold", labelColor)}>Tune the glass</div>
              {([
                { label: "Blur",         value: blur,       set: setBlur,       min: 2,   max: 40,  unit: "px" },
                { label: "Saturate",     value: saturate,   set: setSaturate,   min: 100, max: 300, unit: "%" },
                { label: "Brightness",   value: brightness, set: setBrightness, min: 95,  max: 130, unit: "%" },
                { label: "Fill opacity", value: fill,       set: setFill,       min: 0,   max: 50,  unit: "%" },
                { label: "Rim strength", value: rim,        set: setRim,        min: 5,   max: 90,  unit: "%" },
              ] as { label: string; value: number; set: (v: number) => void; min: number; max: number; unit: string }[]).map(({ label, value, set, min, max, unit }) => (
                <div key={label}>
                  <div className="flex justify-between mb-1">
                    <span className={cn("text-xs", labelColor)}>{label}</span>
                    <span className={cn("font-mono text-xs", labelColor)}>{value}{unit}</span>
                  </div>
                  <input type="range" min={min} max={max} value={value}
                    onChange={e => set(Number(e.target.value))}
                    className="w-full accent-green-500" />
                </div>
              ))}
            </div>
            <ControlPanel cfg={tunableCfg} isDark={isDark} />
          </div>
        )}

        {/* ── TUNE BG ── */}
        {tab === "bg-tune" && (
          <div className="flex gap-10 items-start">
            <div className="w-60 space-y-4">
              <div className="mb-1 text-xs font-semibold text-white/60">Tune the background</div>
              {([
                { label: "Base hue",       value: bgHue,       set: setBgHue,       min: 0,  max: 360, unit: "°" },
                { label: "Base lightness", value: bgLightness, set: setBgLightness, min: 2,  max: 20,  unit: "%" },
                { label: "Green blob",     value: bgGreen,     set: setBgGreen,     min: 0,  max: 80,  unit: "%" },
                { label: "Blue blob",      value: bgBlue,      set: setBgBlue,      min: 0,  max: 80,  unit: "%" },
                { label: "Purple blob",    value: bgPurple,    set: setBgPurple,    min: 0,  max: 80,  unit: "%" },
                { label: "Orange blob",    value: bgOrange,    set: setBgOrange,    min: 0,  max: 80,  unit: "%" },
              ] as { label: string; value: number; set: (v: number) => void; min: number; max: number; unit: string }[]).map(({ label, value, set, min, max, unit }) => (
                <div key={label}>
                  <div className="flex justify-between mb-1">
                    <span className="text-xs text-white/60">{label}</span>
                    <span className="font-mono text-xs text-white/60">{value}{unit}</span>
                  </div>
                  <input type="range" min={min} max={max} value={value}
                    onChange={e => set(Number(e.target.value))}
                    className="w-full accent-green-500" />
                </div>
              ))}
              {/* CSS output */}
              <div className="mt-4 rounded-xl bg-black/30 p-3">
                <div className="mb-1 text-[10px] font-semibold text-white/40 uppercase tracking-wider">CSS output</div>
                <pre className="whitespace-pre-wrap break-all font-mono text-[9px] leading-relaxed text-white/50">{tunableBgCss}</pre>
              </div>
            </div>
            <ControlPanel cfg={tunableCfg} isDark={true} />
          </div>
        )}
      </div>
    </div>
  );
}
